// Pure logic for the mobile-web "get the app" banner: dismissal persistence, the
// iOS-Safari suppression rule, the mobile platform guess, and the shared banner
// height. NO runtime imports on purpose, so the unit test exercises it without
// dragging the app's dependency graph (see orion-web/CLAUDE.md → Tests). The hook
// that composes these with the "never installed" gate lives in `use-app-banner.ts`.

/**
 * The banner's height, in px. The flow banner is exactly this tall and AppFrame
 * shrinks <main> by the same amount (via `--app-banner-h`), so the two always sum
 * to the viewport — one constant feeds both, so they can't drift apart.
 */
export const APP_BANNER_HEIGHT_PX = 64;

// A NEW persisted key, so it does not inherit the `orion-web:` prefix — it is named
// for the product (see orion-web/CLAUDE.md → "Why Orion still appears").
const DISMISSED_KEY = 'symphony-app-banner-dismissed';

/** Whether this browser has dismissed the banner. No storage → false (let it show;
 *  the component's own state still hides it for the session after a tap). */
export function hasDismissedAppBanner(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function markAppBannerDismissed(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DISMISSED_KEY, '1');
  } catch {
    // private mode — the component's in-session state suppresses the repeat
  }
}

/**
 * The custom banner exists for the browsers Apple's native Smart App Banner does NOT
 * reach. On iOS Safari the `apple-itunes-app` <meta> (SymphonyApp) already draws the
 * real thing, so a second, home-grown strip there is pure redundancy — suppress it.
 * Only Safari on iOS honours that meta; iOS Chrome/Firefox/Edge/Opera (CriOS/FxiOS/
 * EdgiOS/OPiOS) and Google's in-app browser (GSA) do not, so they keep the custom one.
 */
export function isIosSafariUA(ua: string): boolean {
  const iOS = /iPhone|iPod|iPad/.test(ua);
  if (!iOS) return false;
  const nonSafariIos = /CriOS|FxiOS|EdgiOS|OPiOS|GSA/.test(ua);
  return !nonSafariIos && /Safari/.test(ua);
}

export function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  return isIosSafariUA(navigator.userAgent);
}

/** Best-effort store target for the download-attribution event. The smart link itself
 *  device-detects, so this only labels the analytics; unknown falls back to android. */
export function mobilePlatformUA(ua: string): 'ios' | 'android' {
  return /iPhone|iPod|iPad/.test(ua) ? 'ios' : 'android';
}

export function mobilePlatform(): 'ios' | 'android' {
  if (typeof navigator === 'undefined') return 'android';
  return mobilePlatformUA(navigator.userAgent);
}
